/**
 * WorkflowExecutionDetail Component
 *
 * Displays detailed information about a workflow execution.
 */
import { Link, useNavigate } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { Button } from '../ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../ui/card';
import { useWorkflowExecution } from '../../hooks/useWorkflows';
import { formatCents, formatTokens } from '../../hooks/useExecution';
import {
  workflowService,
  WorkflowExecutionDetail as WorkflowExecutionDetailType,
  WorkflowStepExecution,
  StepCounts,
} from '../../services/workflow';

interface WorkflowExecutionDetailProps {
  workflowId: string;
  executionId: string;
}

export function WorkflowExecutionDetail({
  workflowId,
  executionId,
}: WorkflowExecutionDetailProps) {
  const { data: execution, isLoading, error } = useWorkflowExecution(workflowId, executionId);

  if (isLoading) {
    return <ExecutionDetailSkeleton />;
  }

  if (error || !execution) {
    return (
      <Card>
        <CardContent className="py-12 text-center">
          <div className="text-6xl mb-4">❌</div>
          <CardTitle className="mb-2">Execution Not Found</CardTitle>
          <CardDescription className="mb-4">
            The execution you're looking for doesn't exist or has been deleted.
          </CardDescription>
          <Button asChild>
            <Link to="/executions">Back to Executions</Link>
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Back Link */}
      <Link
        to="/executions"
        className="text-sm text-primary hover:underline inline-block"
      >
        ← Back to Executions
      </Link>

      {/* Header Card */}
      <ExecutionHeader execution={execution} />

      {/* Info Cards */}
      <ExecutionInfoCards execution={execution} />

      {/* Input Section */}
      {execution.inputData && Object.keys(execution.inputData).length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Input</CardTitle>
            <CardDescription>Data provided to the workflow</CardDescription>
          </CardHeader>
          <CardContent>
            <pre className="bg-slate-100 p-4 rounded-lg overflow-x-auto text-sm max-h-64">
              {JSON.stringify(execution.inputData, null, 2)}
            </pre>
          </CardContent>
        </Card>
      )}

      {/* Output Section */}
      {execution.outputData && Object.keys(execution.outputData).length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Output</CardTitle>
            <CardDescription>Result from the workflow</CardDescription>
          </CardHeader>
          <CardContent>
            <pre className="bg-slate-100 p-4 rounded-lg overflow-x-auto text-sm max-h-64">
              {JSON.stringify(execution.outputData, null, 2)}
            </pre>
          </CardContent>
        </Card>
      )}

      {/* Error Section */}
      {execution.errorMessage && (
        <Card>
          <CardHeader>
            <CardTitle className="text-red-600">Error</CardTitle>
            <CardDescription>Error information from the execution</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-red-700 font-mono text-sm whitespace-pre-wrap">
                {execution.errorMessage}
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Step Executions */}
      <StepExecutionsSection steps={execution.stepExecutions || []} />
    </div>
  );
}

/**
 * What a tool call returned, summarised by the backend and stored on the run.
 * Counts only — never the payload. A step that processed nothing looks identical
 * to a broken condition unless the run says how much was offered to it.
 */
type ToolResultSummary = {
  ok?: boolean;
  records?: number;
  status?: number;
  error?: string;
  /** Fields the agent asked for that were absent (or null), per field. */
  missing?: Record<string, number>;
};

function ExecutionHeader({ execution }: { execution: WorkflowExecutionDetailType }) {
  const statusConfig = getStatusConfig(execution.status);
  const navigate = useNavigate();
  const replay = useMutation({
    mutationFn: () =>
      workflowService.replayExecution(execution.workflowId, execution.id),
    onSuccess: (created) => {
      toast.success('Replaying workflow', { icon: '🔁' });
      navigate(`/workflows/${execution.workflowId}/executions/${created.id}`);
    },
    onError: (
      e: Error & { response?: { data?: { error?: { message?: string } } } }
    ) => toast.error(e.response?.data?.error?.message || 'Failed to replay workflow'),
  });
  const canReplay = ['COMPLETED', 'FAILED', 'CANCELLED', 'TIMEOUT'].includes(
    execution.status
  );

  return (
    <Card>
      <CardHeader>
        <div className="flex items-start justify-between">
          <div className="flex items-start gap-4">
            <span className="text-5xl">{statusConfig.icon}</span>
            <div>
              <CardTitle className="text-2xl mb-2 flex items-center gap-3">
                Workflow Execution
                <span className={`px-3 py-1 text-sm rounded-full font-medium ${statusConfig.color}`}>
                  {execution.status}
                </span>
              </CardTitle>
              <CardDescription className="text-base">
                Workflow:{' '}
                <Link
                  to={`/workflows/${execution.workflowId}`}
                  className="text-primary hover:underline"
                >
                  {execution.workflowName || execution.workflowId}
                </Link>
              </CardDescription>
              <p className="text-sm text-muted-foreground mt-1 font-mono">
                ID: {execution.id}
              </p>
              <StepCountsLine counts={execution.stepCounts} />
            </div>
          </div>

          <div className="flex gap-2">
            {canReplay && (
              <Button onClick={() => replay.mutate()} disabled={replay.isPending}>
                {replay.isPending ? 'Replaying…' : '🔁 Replay'}
              </Button>
            )}
            <Button variant="outline" asChild>
              <Link to={`/workflows/${execution.workflowId}`}>View Workflow</Link>
            </Button>
          </div>
        </div>
      </CardHeader>
    </Card>
  );
}

function ExecutionInfoCards({ execution }: { execution: WorkflowExecutionDetailType }) {
  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  };

  const formatDuration = (ms: number | null | undefined) => {
    if (!ms) return '-';
    if (ms < 1000) return `${ms}ms`;
    if (ms < 60000) return `${(ms / 1000).toFixed(2)}s`;
    const minutes = Math.floor(ms / 60000);
    const seconds = Math.floor((ms % 60000) / 1000);
    return `${minutes}m ${seconds}s`;
  };

  return (
    <div className="grid gap-4 md:grid-cols-3 lg:grid-cols-6">
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium text-muted-foreground">
            Version
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-2xl font-bold">v{execution.workflowVersion}</p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium text-muted-foreground">
            Duration
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-2xl font-bold">{formatDuration(execution.durationMs)}</p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium text-muted-foreground">
            Tokens
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-2xl font-bold">
            {formatTokens(execution.totalTokensUsed)}
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium text-muted-foreground">
            Cost
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-2xl font-bold">
            {formatCents(execution.totalCostCents)}
          </p>
          <p className="text-xs text-muted-foreground mt-1">Estimated</p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium text-muted-foreground">
            Started
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm font-medium">{formatDate(execution.startedAt)}</p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium text-muted-foreground">
            Completed
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm font-medium">{formatDate(execution.completedAt)}</p>
        </CardContent>
      </Card>
    </div>
  );
}

function StepExecutionsSection({ steps }: { steps: WorkflowStepExecution[] }) {
  if (steps.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Step Executions</CardTitle>
          <CardDescription>Individual step results</CardDescription>
        </CardHeader>
        <CardContent>
          <p className="text-muted-foreground text-center py-8">
            No step executions recorded yet.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Step Executions ({steps.length})</CardTitle>
        <CardDescription>Individual step results</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {steps.map((step, index) => (
            <StepExecutionCard key={step.id} step={step} index={index} />
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

function StepExecutionCard({ step, index }: { step: WorkflowStepExecution; index: number }) {
  const statusConfig = getStatusConfig(step.status);

  const formatDuration = (ms: number | null | undefined) => {
    if (!ms) return '-';
    if (ms < 1000) return `${ms}ms`;
    return `${(ms / 1000).toFixed(2)}s`;
  };

  return (
    <div className="border rounded-lg p-4">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 bg-primary text-white rounded-full flex items-center justify-center text-sm font-medium">
            {index + 1}
          </div>
          <div>
            <p className="font-medium">{step.stepName || step.stepId}</p>
            <p className="text-sm text-muted-foreground">Type: {step.stepType}</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {/* Control steps (delay/transform) call no LLM, so they report no usage. */}
          {step.tokensUsed != null && (
            <span className="text-sm text-muted-foreground">
              {formatTokens(step.tokensUsed)} tokens
            </span>
          )}
          {step.costCents != null && (
            <span className="text-sm text-muted-foreground">
              {formatCents(step.costCents)}
            </span>
          )}
          <span className="text-sm text-muted-foreground">
            {formatDuration(step.durationMs)}
          </span>
          <span className={`px-2 py-1 text-xs rounded-full font-medium ${statusConfig.color}`}>
            {step.status}
          </span>
        </div>
      </div>

      {step.outputData && <StepOutput output={step.outputData as Record<string, unknown>} />}

      {step.errorMessage && (
        <div className="mt-3">
          <p className="text-sm font-medium text-red-600 mb-2">Error:</p>
          <div className="bg-red-50 border border-red-200 rounded p-3">
            <p className="text-red-700 text-sm">{step.errorMessage}</p>
          </div>
        </div>
      )}
    </div>
  );
}

/** A workflow step's output — surfaces text, tools used, and structured data. */
export function StepOutput({ output }: { output: Record<string, unknown> }) {
  const text =
    typeof output.content === 'string'
      ? output.content
      : typeof output.message === 'string'
        ? output.message
        : null;
  const toolCalls = Array.isArray(output.tool_invocations)
    ? (output.tool_invocations as { tool?: string; args?: unknown; result?: ToolResultSummary }[])
    : [];
  const data =
    output.data && typeof output.data === 'object' && !Array.isArray(output.data)
      ? (output.data as Record<string, unknown>)
      : null;

  return (
    <div className="mt-3 space-y-2">
      {text && (
        <div>
          <p className="text-xs font-medium text-muted-foreground mb-1">Output</p>
          <div className="bg-slate-100 p-3 rounded text-xs whitespace-pre-wrap max-h-40 overflow-y-auto">
            {text}
          </div>
        </div>
      )}
      {toolCalls.length > 0 && (
        <div>
          <p className="text-xs font-medium text-muted-foreground mb-1">
            Tools used ({toolCalls.length})
          </p>
          <ul className="space-y-1">
            {toolCalls.map((t, i) => (
              <li key={i} className="text-xs border rounded px-2 py-1 bg-white">
                <span className="font-mono font-medium">{t.tool ?? 'tool'}</span>
                {/* "returned", not just "records": the word names WHICH SIDE the zero
                    came from. AleksGorbatov, 9 Sept 2026 — "leaving the destination
                    side unbuilt is fine as long as the label says which side the zero
                    came from". A bare "0 records" could mean the source handed over
                    nothing or the destination accepted nothing; this claims only the
                    first, which is the only one we can prove. */}
                {typeof t.result?.status === 'number' && t.result.status >= 400 ? (
                  /* Status first: since 8 Oct 2026 a refused call is stored as ok: false,
                     and "HTTP 403" says more than a bare "failed". Older runs still carry
                     ok: true beside the 403 and render the same way. */
                  <span className="ml-1 font-medium text-amber-700">
                    HTTP {t.result.status}
                    {typeof t.result.records === 'number'
                      ? `, ${t.result.records} records returned`
                      : ''}
                  </span>
                ) : t.result?.ok === false ? (
                  <span className="ml-1 text-red-600" title={t.result.error}>
                    failed
                  </span>
                ) : typeof t.result?.records === 'number' ? (
                  <span className="ml-1 font-medium text-slate-700">
                    {t.result.records} records returned
                  </span>
                ) : null}
                {/* A 200 whose upstream renamed a field still reads as "10 records".
                    The field the agent asked for and did not get is the signal.
                    Reddit question, 8 Oct 2026. */}
                {t.result?.missing &&
                  Object.keys(t.result.missing).length > 0 && (
                    <span className="ml-1 font-medium text-amber-700">
                      ·{' '}
                      {Object.entries(t.result.missing)
                        .map(([field, n]) =>
                          typeof t.result?.records === 'number'
                            ? `${field} missing in ${n} of ${t.result.records}`
                            : `${field} missing in ${n}`
                        )
                        .join(', ')}
                    </span>
                  )}
                {t.args != null && (
                  <span className="text-muted-foreground break-all">
                    {' '}
                    {JSON.stringify(t.args)}
                  </span>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
      {data && (
        <div>
          <p className="text-xs font-medium text-muted-foreground mb-1">Structured output</p>
          <pre className="bg-slate-100 p-3 rounded text-xs overflow-x-auto max-h-40">
            {JSON.stringify(data, null, 2)}
          </pre>
        </div>
      )}
      <details>
        <summary className="text-xs text-muted-foreground cursor-pointer">Raw</summary>
        <pre className="mt-1 bg-slate-100 p-3 rounded text-xs overflow-x-auto max-h-40">
          {JSON.stringify(output, null, 2)}
        </pre>
      </details>
    </div>
  );
}

function ExecutionDetailSkeleton() {
  return (
    <div className="space-y-6">
      <div className="h-6 w-32 bg-slate-200 rounded animate-pulse" />
      <div className="h-40 bg-slate-100 rounded-lg animate-pulse" />
      <div className="grid gap-4 md:grid-cols-4">
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className="h-24 bg-slate-100 rounded-lg animate-pulse" />
        ))}
      </div>
      <div className="h-48 bg-slate-100 rounded-lg animate-pulse" />
    </div>
  );
}

/**
 * "COMPLETED" on its own does not say whether anything happened.
 *
 * This line is the difference between a run that did the work and a run whose
 * every branch was skipped — which, until 21 Aug 2026, looked identical here and
 * everywhere else. A skipped step did not even get a database row, so the step
 * list below showed nothing at all and the run read as a clean success.
 *
 * ⚠️ WHY THERE IS NO WARNING BADGE HERE.
 *
 * The first version of this shipped an amber "nothing processed" badge whenever
 * a finished run processed zero steps. An outside reviewer killed it the same
 * day, correctly:
 *
 *   "A zero can be legitimate. Quiet day, nothing to send, COMPLETED with 0
 *    processed is correct. The alert gets teeth when the count is two-sided:
 *    the run says what it processed, the source says what it handed over, and
 *    the two have to tie out."
 *
 * A scheduled workflow with nothing to do processes nothing every quiet day. A
 * badge that fires on all of them is noise, and noise gets muted — so it would
 * not be there on the day it mattered. That is a worse outcome than showing
 * nothing, because it looks like coverage.
 *
 * So this renders the counts as plain fact and lets a human judge. The numbers
 * are always true; the judgement was not.
 *
 * To earn the badge back we need the source side: how many records the source
 * actually handed over. `http_request` already computes it (`records: N` when
 * selecting from a list endpoint) and throws it away — tool results are not
 * persisted per step. Store those, and "processed 0 of 50 offered" becomes a
 * real alert instead of a guess.
 */
export function StepCountsLine({ counts }: { counts?: StepCounts }) {
  if (!counts || counts.total === 0) return null;

  return (
    <div className="mt-2 flex flex-wrap items-center gap-2 text-sm">
      <span className="text-muted-foreground">
        <strong className="text-foreground">{counts.processed}</strong> processed
      </span>
      {counts.skipped > 0 && (
        <span className="text-muted-foreground">
          · <strong className="text-foreground">{counts.skipped}</strong> skipped
        </span>
      )}
      {counts.failed > 0 && (
        <span className="text-red-600">
          · <strong>{counts.failed}</strong> failed
        </span>
      )}
      <span className="text-muted-foreground">of {counts.total}</span>
    </div>
  );
}

/**
 * Status badge config, covering EVERY value the backend can send.
 *
 * ⚠️ Two were missing until 23 Aug 2026, and the fallback hid both:
 *
 *   SKIPPED  (`StepExecutionStatus`) — shipped 21 Aug when skipped steps
 *            started writing rows. Without a config the row fell through to
 *            PENDING and a skipped step rendered "⏳ Pending" inside a run
 *            that had already finished.
 *   TIMEOUT  (`WorkflowExecutionStatus`) — set by the stuck-execution reaper.
 *            A killed run also read as "⏳ Pending", i.e. still going.
 *
 * The fallback was the real defect. `|| configs.PENDING` does not mean "we do
 * not know", it asserts a specific and wrong state, so adding a status to the
 * backend enum silently mislabels it here rather than showing up as missing.
 * The default now renders the raw status and admits it is unmapped — which is
 * ugly on purpose, because ugly gets fixed and plausible does not.
 *
 * Keep this in sync with `StepExecutionStatus` and `WorkflowExecutionStatus`
 * in `backend/.../db/models.py`.
 */
export function getStatusConfig(status: string) {
  const configs: Record<string, { icon: string; color: string }> = {
    PENDING: { icon: '⏳', color: 'bg-yellow-100 text-yellow-800' },
    RUNNING: { icon: '🔄', color: 'bg-blue-100 text-blue-800' },
    COMPLETED: { icon: '✅', color: 'bg-green-100 text-green-800' },
    FAILED: { icon: '❌', color: 'bg-red-100 text-red-800' },
    CANCELLED: { icon: '🚫', color: 'bg-gray-100 text-gray-800' },
    PAUSED: { icon: '⏸️', color: 'bg-orange-100 text-orange-800' },
    SKIPPED: { icon: '⏭️', color: 'bg-slate-100 text-slate-700' },
    TIMEOUT: { icon: '⏰', color: 'bg-red-100 text-red-800' },
  };
  return configs[status] || { icon: '❔', color: 'bg-gray-100 text-gray-800' };
}

export default WorkflowExecutionDetail;
